import { tagFirestoreError } from '../firestoreErrorContext'

export const dim = 'color:#888'
export const bold = 'color:#f59e0b;font-weight:bold'

const OP_W = 17 // 'permission-denied'.length

function makeBatcher<T>(flush: (batch: T[]) => void): (entry: T) => void {
  let batch: T[] = []
  let timer: ReturnType<typeof setTimeout> | null = null
  return (entry: T) => {
    batch.push(entry)
    if (!timer)
      timer = setTimeout(() => {
        timer = null
        const b = batch
        batch = []
        flush(b)
      }, 0)
  }
}

export function hdr(op: string, detail: string) {
  return `[fs] ${op.padEnd(OP_W)} ${detail}`
}

function dimGroup(op: string, paths: string[]) {
  const parts = paths.map(p => {
    const i = p.indexOf('  (')
    return i >= 0 ? ([p.slice(0, i), p.slice(i)] as const) : ([p, ''] as const)
  })
  const w = Math.max(...parts.map(([l]) => l.length))
  console.groupCollapsed(`%c${hdr(op, `(${paths.length})`)}`, dim)
  parts.forEach(([l, r]) => console.log(`%c${l.padEnd(w)}${r}`, dim))
  console.groupEnd()
}

export const logGrace = makeBatcher<string>(b => dimGroup('grace', b))
export const logRevive = makeBatcher<string>(b => dimGroup('revive', b))
export const logReuse = makeBatcher<string>(b => dimGroup('reuse', b))
export const logUnsub = makeBatcher<string>(b => dimGroup('unsubscribe', b))
export const logPermDenied = makeBatcher<string>(b => dimGroup('permission-denied', b))
export const logSubscribe = makeBatcher<string>(b => dimGroup('subscribe', b))
function boldGroup(op: string, paths: string[]) {
  const w = Math.max(...paths.map(p => p.length))
  console.groupCollapsed(`%c${hdr(op, `(${paths.length})`)}`, bold)
  paths.forEach(p => console.log(`%c${p.padEnd(w)}`, bold))
  console.groupEnd()
}

export const logAddDoc = makeBatcher<string>(b => boldGroup('addDoc', b))
export const logUpdateDoc = makeBatcher<string>(b => boldGroup('updateDoc', b))
export const logDeleteDoc = makeBatcher<string>(b => boldGroup('deleteDoc', b))
export const logSetDoc = makeBatcher<string>(b => boldGroup('setDoc', b))

type GetDocResult = { path: string; exists: boolean; fromCache: boolean }
export const logGetDocRes = makeBatcher<GetDocResult>(batch => {
  const [net, cached] = [batch.filter(e => !e.fromCache), batch.filter(e => e.fromCache)]
  if (net.length) {
    const w = Math.max(...net.map(e => e.path.length))
    console.groupCollapsed(`%c${hdr('getDoc', `(${net.length})`)}`, bold)
    net.forEach(({ path, exists }) =>
      console.log(`%c${path.padEnd(w)}  (${exists ? 'exists' : 'missing'})`, bold),
    )
    console.groupEnd()
  }
  if (cached.length) {
    const w = Math.max(...cached.map(e => e.path.length))
    console.groupCollapsed(`%c${hdr('getDoc', `(${cached.length}, cache)`)}`, dim)
    cached.forEach(({ path, exists }) =>
      console.log(`%c${path.padEnd(w)}  (${exists ? 'exists' : 'missing'}, cache)`, dim),
    )
    console.groupEnd()
  }
})

type GetDocsResult = { path: string; n: number; fromCache: boolean }
export const logGetDocsRes = makeBatcher<GetDocsResult>(batch => {
  const [net, cached] = [batch.filter(e => !e.fromCache), batch.filter(e => e.fromCache)]
  if (net.length) {
    const w = Math.max(...net.map(e => e.path.length))
    const total = net.reduce((s, e) => s + e.n, 0)
    console.groupCollapsed(`%c${hdr('getDocs', `(${total} docs)`)}`, bold)
    net.forEach(({ path, n }) =>
      console.log(`%c${path.padEnd(w)}  (${n} doc${n !== 1 ? 's' : ''})`, bold),
    )
    console.groupEnd()
  }
  if (cached.length) {
    const w = Math.max(...cached.map(e => e.path.length))
    const total = cached.reduce((s, e) => s + e.n, 0)
    console.groupCollapsed(`%c${hdr('getDocs', `(${total} docs, cache)`)}`, dim)
    cached.forEach(({ path, n }) =>
      console.log(`%c${path.padEnd(w)}  (${n} doc${n !== 1 ? 's' : ''}, cache)`, dim),
    )
    console.groupEnd()
  }
})

type SnapshotEntry = { path: string; n: number; subs: number; fromCache: boolean }
export const logSnapshot = makeBatcher<SnapshotEntry>(batch => {
  const [net, cached] = [batch.filter(e => !e.fromCache), batch.filter(e => e.fromCache)]
  if (net.length) {
    const w = Math.max(...net.map(e => e.path.length))
    const total = net.reduce((s, e) => s + e.n, 0)
    console.groupCollapsed(`%c${hdr('snapshot', `(${total} docs)`)}`, bold)
    net.forEach(({ path, n, subs }) =>
      console.log(
        `%c${path.padEnd(w)}  (${n} doc${n !== 1 ? 's' : ''}, ${subs} sub${subs !== 1 ? 's' : ''})`,
        bold,
      ),
    )
    console.groupEnd()
  }
  if (cached.length) {
    const w = Math.max(...cached.map(e => e.path.length))
    const total = cached.reduce((s, e) => s + e.n, 0)
    console.groupCollapsed(`%c${hdr('snapshot', `(${total} docs, cache)`)}`, dim)
    cached.forEach(({ path, n }) =>
      console.log(`%c${path.padEnd(w)}  (${n} doc${n !== 1 ? 's' : ''}, cache)`, dim),
    )
    console.groupEnd()
  }
})

export const rethrowTagged = (op: string, path: string) => (err: unknown): never => {
  throw tagFirestoreError(err, op, path)
}

export function refPath(ref: unknown): string {
  const r = ref as { path?: string; _query?: { path?: { toString(): string } } }
  if (r.path) return r.path // DocumentReference / CollectionReference
  if (r._query?.path?.toString) return r._query.path.toString() // Query — internal path
  return '(query)'
}
